import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blurOnWheel } from '../../lib/number-input';
import { leftOnSiteError, LEFT_ON_SITE_MAX, LEFT_ON_SITE_MIN } from '../../lib/service-policies';
import type { MaintenanceAgentSettings } from '../../types/service-policies';

/**
 * The maintenance agent's company-wide thresholds (FE-5): the left-on-site
 * alert days (D5, default 7) and whether inspections are tracked in FieldIQ at
 * all.
 *
 * Rendered on Settings → Maintenance agent (FE-4). It sat on the Equipment
 * Types page until that tab existed, which is where the FE-5 brief put it.
 *
 * GET is admin + office staff; PUT is STRICT admin (api#994). Anyone else who
 * can read sees the number, read-only.
 *
 * "INSPECTIONS NOT TRACKED IN FIELDIQ" (operator, 2026-10-07): with no cadence
 * policy and no inspection form submitted in the last 90 days, the agent's
 * inspection rule raises nothing for this company by design. Saying so beats an
 * empty, healthy-looking state — and it is only said after a successful load.
 */

export const MAINTENANCE_SETTINGS_KEY = ['maintenance-agent-settings'] as const;

/** One query for the tab: the Mode section and this card share it. */
export function useMaintenanceAgentSettings() {
  return useQuery({
    queryKey: MAINTENANCE_SETTINGS_KEY,
    queryFn: async () => unwrap<MaintenanceAgentSettings>((await api.maintenanceAgent.settings()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';

export default function MaintenanceAgentSettingsCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const query = useMaintenanceAgentSettings();
  const failure = queryLoadFailure(query);
  const [days, setDays] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.leftOnSiteDays != null) setDays(String(query.data.leftOnSiteDays));
  }, [query.data?.leftOnSiteDays]);

  const save = useMutation({
    mutationFn: (n: number) => api.maintenanceAgent.updateSettings({ left_on_site_days: n }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_SETTINGS_KEY });
    },
    onError: (err: any) => setError(err?.response?.data?.error || 'Failed to save'),
  });

  const submit = () => {
    setSaved(false);
    const problem = leftOnSiteError(days);
    if (problem) { setError(problem); return; }
    setError(null);
    save.mutate(Number(days));
  };

  const s = query.data;

  return (
    <section aria-labelledby="maintenance-agent-thresholds" className={PANEL} data-testid="maintenance-agent-settings">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Thresholds</p>
        <h2 id="maintenance-agent-thresholds" className="text-base font-semibold text-white">Inspections and left on site</h2>
      </div>
      <div className="px-4 pb-4 space-y-3">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="maintenance settings" allowed="admins and office staff" />
        ) : query.isLoading || !s ? (
          <span role="status" aria-label="Loading maintenance settings" className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" />
        ) : (
          <>
            {s.inspections && !s.inspections.tracked && (
              <div role="note" className="flex items-start gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm">
                <Info size={14} aria-hidden className="mt-0.5 shrink-0 text-fieldiq-muted" />
                <div>
                  <p className="font-semibold text-fieldiq-text">Inspections not tracked in FieldIQ</p>
                  <p className="mt-0.5 text-xs text-fieldiq-text">
                    No inspection form was submitted in the last {s.inspections.windowDays} days and no equipment type
                    has an inspection cadence, so no inspection is ever shown as due. To turn them on, add an inspection
                    cadence to a type in{' '}
                    <Link to="/settings/equipment-types" className="font-semibold underline underline-offset-2">Equipment Types</Link>.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="los-days" className="text-sm text-fieldiq-text">Flag equipment left on site after</label>
              {isAdmin ? (
                <>
                  <input
                    id="los-days"
                    type="number"
                    min={LEFT_ON_SITE_MIN}
                    max={LEFT_ON_SITE_MAX}
                    step={1}
                    onWheel={blurOnWheel}
                    value={days}
                    onChange={(e) => { setDays(e.target.value); setError(null); setSaved(false); }}
                    className="w-20 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white text-sm"
                  />
                  <span className="text-sm text-fieldiq-text">days</span>
                  <button
                    onClick={submit}
                    disabled={save.isPending}
                    className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
                  >
                    Save
                  </button>
                  {saved && <span className="text-xs text-fieldiq-text">Saved</span>}
                </>
              ) : (
                <span id="los-days" className="text-sm text-white">{s.leftOnSiteDays ?? '—'} days</span>
              )}
            </div>
            {error && <p role="alert" className="text-xs text-fieldiq-text">{error}</p>}
          </>
        )}
      </div>
      {!failure && (
        <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
          FieldIQ default: 7 days.{!isAdmin && ' Only an admin can change it.'}
        </p>
      )}
    </section>
  );
}
